import classNames from 'classnames'

import ErrorDisplay from '../../../components/error/ErrorDisplay'
import { NodeData, Tree, RunInfo } from './NodeData'
import styles from './RunInfoView.module.css'
import CloseOutlinedIcon from '@mui/icons-material/CloseOutlined'
import Loading from '../../../Loading'

function hasKeys(m: any) {
  if (typeof m !== 'object') {
    return false
  }
  return Object.keys(m).length > 0
}

/**
 * Renders the view for displaying run information.
 *
 * @param {object} props - The component's props.
 * @param {Tree} props.tree - The tree data.
 * @param {RunInfo} props.runInfo - The run information.
 * @param {Error | null} props.runError - The run error, if any.
 * @param {boolean} props.isRunning - Indicates if the run is in progress.
 * @param {Elements<NodeData>} props.allNodes - All available nodes.
 * @param {Function} props.close - Callback to close the view.
 * @returns {JSX.Element} The rendered component.
 */
export default function RunInfoView({
  tree,
  runInfo,
  runError,
  isRunning,
  allNodes,
  close,
}: {
  tree: Tree
  runInfo: RunInfo
  runError: Error | null
  isRunning: boolean
  allNodes: any
  close: () => void
}) {
  return (
    <div className={styles.runViewContent}>
      <button title="close" className={classNames('bstdioEmojiBtn', styles.closeBtn)} onClick={() => close()}>
        <CloseOutlinedIcon />
      </button>

      {/* RUN REQUEST FAILED */}
      {runError !== null ? <ErrorDisplay err={runError} /> : null}

      {/* RUN NOT FINISHED */}
      {isRunning ? (
        <div>
          <Loading action={'Running config...'} />
        </div>
      ) : null}

      {/* NODE NOT SELECTED AND RUN SUCCEEDED */}
      {runInfo !== null ? (
        <div>
          <h4>Run Overview</h4>
          <div className={styles.runOverviewRow}>
            <div>Messages consumed</div>
            <code>{runInfo.result?.summary?.input || 0} </code>
          </div>
          <div className={styles.runOverviewRow}>
            <div>Messages written</div>
            <code>{runInfo.result?.summary?.output || 0}</code>
          </div>
          <div className={styles.runOverviewRow}>
            <div>Processing errors</div>
            <code>{runInfo.result?.summary?.processor_errors || 0}</code>
          </div>
          <br />
          {(allNodes || []).length === 0 ? (
            <p className={styles.errorText}>There doesnt appear to be anything to run yet. Create some stuff and then come back.</p>
          ) : null}
          {runInfo.result?.error ? (
            <div className={styles.errorText}>
              <div>Error:</div>
              <code>{runInfo.result?.error || ''}</code>
            </div>
          ) : null}
          {runInfo.result?.force_closed ? (
            <p className={styles.warningText}>
              Warning: The stream failed to finish within an alloted time, this could be due to a component preventing shutdown (like an
              unfinished batcher).
            </p>
          ) : null}
          <p>
            Click on a component with a <span title="has events" className={styles.hasEventsExample}></span> marker to view messages that
            flowed through it.
          </p>
        </div>
      ) : null}

      {/* NODE NOT SELECTED AND COMPONENTS NEED MOCKING */}
      {runInfo !== null && (hasKeys(runInfo?.result?.missing_inputs) || hasKeys(runInfo?.result?.missing_processors)) ? (
        <div>
          <h4 className={styles.actionNeededTitle}>Action needed</h4>
          <p>
            Some components are present that cannot be run by the server, in order to meaningfully test your config they should be mocked.
          </p>
          {tree.read_only ? null : (
            <p>
              Click each component with a <span title="action needed" className={styles.needsActionExample}></span> marker in order to set a
              mock behaviour.
            </p>
          )}
        </div>
      ) : null}
    </div>
  )
}
